import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { Button } from './Button.jsx'

const AlertContext = createContext(null)

function ModalShell({ open, title, children, onClose }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-0 flex items-end justify-center p-4 sm:items-center" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl sm:max-h-[80vh]" onMouseDown={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
            <div className="text-sm font-bold text-slate-900">{title}</div>
            <button
              type="button"
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
              onClick={onClose}
            >
              Fermer
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        </div>
      </div>
    </div>
  )
}

export function AlertProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    variant: 'alert',
    title: 'Information',
    message: '',
    confirmText: 'OK',
    cancelText: 'Annuler',
    resolve: null,
  })

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }))
  }, [])

  const alert = useCallback(({ title = 'Information', message = '', confirmText = 'OK' } = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        variant: 'alert',
        title,
        message,
        confirmText,
        cancelText: 'Annuler',
        resolve,
      })
    })
  }, [])

  const confirm = useCallback(({ title = 'Confirmation', message = '', confirmText = 'Confirmer', cancelText = 'Annuler' } = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        variant: 'confirm',
        title,
        message,
        confirmText,
        cancelText,
        resolve,
      })
    })
  }, [])

  const value = useMemo(() => ({ alert, confirm }), [alert, confirm])

  return (
    <AlertContext.Provider value={value}>
      {children}
      <ModalShell
        open={state.open}
        title={state.title}
        onClose={() => {
          if (state.variant === 'confirm') state.resolve?.(false)
          else state.resolve?.(true)
          close()
        }}
      >
        <div className="grid gap-4">
          <div className="text-sm text-slate-700">{state.message}</div>
          <div className="flex items-center justify-end gap-2">
            {state.variant === 'confirm' ? (
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  state.resolve?.(false)
                  close()
                }}
              >
                {state.cancelText}
              </Button>
            ) : null}
            <Button
              variant={state.variant === 'confirm' ? 'danger' : 'primary'}
              type="button"
              onClick={() => {
                state.resolve?.(true)
                close()
              }}
            >
              {state.confirmText}
            </Button>
          </div>
        </div>
      </ModalShell>
    </AlertContext.Provider>
  )
}

export function useAlert() {
  const ctx = useContext(AlertContext)
  if (!ctx) {
    throw new Error('useAlert must be used within AlertProvider')
  }
  return ctx
}
